import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UzBadge, UzButton, UzDialog, UzTextarea } from '@utazzo/ui';
import { Check, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';

import {
  CATALOG_ACTION_LABEL,
  CATALOG_STATUS_LABEL,
  TYPE_LABEL,
  formatWhen,
  statusTone,
} from '../catalog-approval-format';
import {
  approveCatalogItem,
  getCatalogApprovalHistory,
  rejectCatalogItem,
  type PendingApprovalItem,
} from '../catalog-approvals.api';

import { humanEnum } from '@/lib/enum-labels';

export type CatalogApprovalReviewAction = 'approve' | 'reject';

/**
 * 2026-08-06. A rejection must say why. The approval note stays optional - an
 * approval is self-explanatory - but a rejection sends the product back to the
 * submitter, and the note is the only place the reason is recorded (it is what
 * the approval trail shows them). Enforced again on the API so a direct call
 * cannot skip it.
 */
export const REJECTION_REASON_MAX = 500;
export const REJECTION_REASON_REQUIRED = 'Tell the submitter why this is being rejected.';

/** `null` when the reason is acceptable, otherwise the message to show. */
export const rejectionReasonError = (value: string): string | null =>
  value.trim().length === 0 ? REJECTION_REASON_REQUIRED : null;

/**
 * 2026-08-05. Detail + review dialog for Product approvals - the counterpart of
 * `LeadApprovalReviewDialog` on the lead-approvals inbox, so both queues read
 * the same way: an Info button (and row-click) opens the full request context,
 * and a reviewer can Approve / Reject without closing it.
 *
 * The catalog queue has no assigned reviewer (unlike lead approvals, where a
 * request is routed to one team lead) - any admin holding the approval
 * permission decides, so the reviewer reads "Admin pool" until a decision is
 * taken. What the modal adds over the row is the approval trail from
 * `catalog_approval_logs`, which names who submitted / decided and when.
 */

interface CatalogApprovalReviewDialogProps {
  /** The open queue row, or null when nothing is open. */
  item: PendingApprovalItem | null;
  /**
   * Skip the detail view and open straight on the confirmation step - used by
   * the per-row Approve / Reject buttons.
   */
  initialAction?: CatalogApprovalReviewAction | null;
  onClose: () => void;
  /** Left-hand footer slot; the queue passes an "Open product" button. */
  footerLeft?: React.ReactNode;
}

export const CatalogApprovalReviewDialog = ({
  item,
  initialAction = null,
  onClose,
  footerLeft,
}: CatalogApprovalReviewDialogProps): React.JSX.Element => {
  const qc = useQueryClient();
  const [action, setAction] = useState<CatalogApprovalReviewAction | null>(initialAction);
  const [note, setNote] = useState('');
  // The missing-reason error only shows once Confirm has been pressed - an
  // empty field is not an error the moment the dialog opens.
  const [noteTouched, setNoteTouched] = useState(false);

  // Re-seed whenever the caller opens a different row (or opens the same one
  // straight on Approve / Reject) so a stale note never carries over.
  useEffect(() => {
    setAction(initialAction);
    setNote('');
    setNoteTouched(false);
  }, [item?.entityType, item?.id, initialAction]);

  // A row that has already been decided is read-only; the All view mixes both.
  const isPending = Boolean(item && !item.decidedAt);

  // Broad key: a decision moves the row between the Pending / Approved /
  // Rejected slices, and each slice is its own cache entry.
  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ['catalog-approvals'] });
  };
  const approve = useMutation({
    mutationFn: () =>
      approveCatalogItem(item!.entityType, item!.id, { note: note.trim() || undefined }),
    onSuccess: () => {
      invalidate();
      toast.success('Product approved');
      onClose();
    },
    onError: () => toast.error('Failed to approve this product'),
  });
  const reject = useMutation({
    mutationFn: () => rejectCatalogItem(item!.entityType, item!.id, { note: note.trim() }),
    onSuccess: () => {
      invalidate();
      toast.success('Product rejected');
      onClose();
    },
    onError: () => toast.error('Failed to reject this product'),
  });
  const submitting = approve.isPending || reject.isPending;

  const isReject = action === 'reject';
  const reasonError = isReject ? rejectionReasonError(note) : null;

  const confirm = (): void => {
    if (isReject) {
      // Keep the dialog open and point at the field rather than firing a
      // rejection the submitter cannot act on.
      setNoteTouched(true);
      if (reasonError) return;
      reject.mutate();
      return;
    }
    approve.mutate();
  };

  return (
    <>
      <UzDialog
        open={item !== null && action === null}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
        title={item ? `${TYPE_LABEL[item.entityType]} - ${item.name}` : 'Approval details'}
        description={
          item
            ? isPending
              ? 'Review the request context below. Approve / Reject act on this product.'
              : `This product was ${item.status === 'ACTIVE' ? 'approved' : 'decided'} - shown for reference.`
            : undefined
        }
        size="lg"
        footer={
          item ? (
            <div className="flex w-full items-center justify-between gap-2">
              <div className="flex items-center">{footerLeft}</div>
              <div className="flex gap-2">
                <UzButton variant="ghost" onClick={onClose}>
                  Close
                </UzButton>
                {isPending && (
                  <>
                    <UzButton
                      variant="outline"
                      onClick={() => {
                        setNote('');
                        setNoteTouched(false);
                        setAction('reject');
                      }}
                    >
                      <X className="mr-1 h-4 w-4" />
                      Reject
                    </UzButton>
                    <UzButton
                      onClick={() => {
                        setNote('');
                        setNoteTouched(false);
                        setAction('approve');
                      }}
                    >
                      <Check className="mr-1 h-4 w-4" />
                      Approve
                    </UzButton>
                  </>
                )}
              </div>
            </div>
          ) : null
        }
      >
        {item && <CatalogApprovalDetailContent row={item} />}
      </UzDialog>

      <UzDialog
        open={item !== null && action !== null}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
        title={`${isReject ? 'Reject' : 'Approve'} ${item?.name ?? 'product'}`}
        description={
          isReject
            ? 'The captured edit is discarded and the live version stays as it is. A product awaiting its first approval goes back to Draft. The reason below is recorded on the approval trail.'
            : 'The product goes live immediately after you confirm. A captured edit, if any, is applied to the live version.'
        }
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <UzButton variant="ghost" onClick={onClose} disabled={submitting}>
              Cancel
            </UzButton>
            <UzButton
              variant={isReject ? 'outline' : 'default'}
              onClick={confirm}
              loading={submitting}
            >
              {isReject ? 'Confirm rejection' : 'Confirm approval'}
            </UzButton>
          </div>
        }
      >
        {item && (
          <div className="space-y-3 text-sm text-zinc-700">
            <p>
              {TYPE_LABEL[item.entityType]}: <strong>{item.name}</strong>
            </p>
            <p>
              {item.hasPendingChange ? 'Edit pending on an approved product' : 'New product'},
              submitted by <strong>{item.submittedByName ?? 'Unknown'}</strong>
            </p>
            <UzTextarea
              name="note"
              label={isReject ? 'Rejection reason' : 'Reviewer note (optional)'}
              placeholder={
                isReject ? 'What needs to change before this can be approved?' : undefined
              }
              required={isReject}
              hint={isReject ? 'Shown to the submitter and kept on the approval trail.' : undefined}
              error={noteTouched && reasonError ? reasonError : undefined}
              maxLength={isReject ? REJECTION_REASON_MAX : undefined}
              showCount={isReject}
              rows={3}
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
                if (noteTouched) setNoteTouched(false);
              }}
              disabled={submitting}
            />
          </div>
        )}
      </UzDialog>
    </>
  );
};

/**
 * Read-only detail block: every field the queue row carries, plus the approval
 * trail - which is where "who reviewed this" actually lives for a catalog item.
 */
export const CatalogApprovalDetailContent = ({
  row,
}: {
  row: PendingApprovalItem;
}): React.JSX.Element => {
  const historyQuery = useQuery({
    queryKey: ['catalog-approvals', 'history', row.entityType, row.id],
    queryFn: () => getCatalogApprovalHistory(row.entityType, row.id),
  });
  const history = historyQuery.data ?? [];

  return (
    <div className="space-y-4 text-sm text-zinc-700">
      <div className="flex flex-wrap items-center gap-2">
        <UzBadge tone={statusTone(row.status)} pill>
          {humanEnum(CATALOG_STATUS_LABEL, row.status)}
        </UzBadge>
        <UzBadge tone="secondary" pill>
          {TYPE_LABEL[row.entityType]}
        </UzBadge>
        {row.hasPendingChange && (
          <UzBadge tone="warning" pill>
            Edit pending
          </UzBadge>
        )}
      </div>

      <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
        <DetailRow label="Product" value={row.name} />
        <DetailRow label="Type" value={TYPE_LABEL[row.entityType]} />
        <DetailRow
          label="Location"
          value={[row.city, row.country].filter(Boolean).join(', ') || '-'}
        />
        <DetailRow label="Requested by" value={row.submittedByName ?? '-'} />
        <DetailRow label="Submitted" value={formatWhen(row.submittedAt)} />
        <DetailRow
          label="Reviewer"
          // No per-item routing on the catalog queue - anyone with the approval
          // permission can decide, so an undecided row belongs to the pool.
          value={row.decidedByName ?? (row.decidedAt ? 'Unknown' : 'Admin pool')}
        />
        {row.decidedAt && <DetailRow label="Reviewed" value={formatWhen(row.decidedAt)} />}
      </dl>

      <div className="rounded-md border border-zinc-200 bg-zinc-50/60 px-4 py-3">
        <p className="text-[10px] font-semibold tracking-wider text-zinc-400 uppercase">
          Requested change
        </p>
        <p className="mt-1 text-sm text-zinc-800">
          {row.hasPendingChange ? (
            <>
              <strong>Edit pending</strong>
              <span className="mt-1 block text-xs text-zinc-500">
                The approved version stays live until this edit is approved.
              </span>
            </>
          ) : (
            <>
              <strong>New product</strong>
              <span className="mt-1 block text-xs text-zinc-500">
                Goes live once approved; a rejection sends it back to Draft.
              </span>
            </>
          )}
        </p>
      </div>

      <div className="rounded-md border border-zinc-200 bg-white px-4 py-3">
        <p className="text-[10px] font-semibold tracking-wider text-zinc-400 uppercase">
          Approval trail
        </p>
        {historyQuery.isLoading ? (
          <p className="mt-1 text-sm text-zinc-500">Loading...</p>
        ) : history.length === 0 ? (
          <p className="mt-1 text-sm text-zinc-500">Nothing recorded yet.</p>
        ) : (
          <ul className="mt-2 space-y-2">
            {history.map((entry) => (
              <li key={entry.id} className="flex items-start justify-between gap-3">
                <div>
                  <span className="text-sm text-zinc-800">
                    {humanEnum(CATALOG_ACTION_LABEL, entry.action)}
                  </span>
                  <span className="block text-xs text-zinc-500">
                    {entry.actorName ?? 'System'}
                    {entry.note ? ` - "${entry.note}"` : ''}
                  </span>
                </div>
                <span className="shrink-0 text-xs text-zinc-500">
                  {formatWhen(entry.createdAt)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const DetailRow = ({ label, value }: { label: string; value: string }): React.JSX.Element => (
  <div className="flex flex-col gap-0.5">
    <dt className="text-[10px] font-semibold tracking-wider text-zinc-400 uppercase">{label}</dt>
    <dd className="truncate text-sm text-zinc-800">{value}</dd>
  </div>
);
